import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../auth/context/AuthProvider";
import "./Home.css";
import { getRelatorioDiario } from "../relatoriodiario/api";

/* ---------------- helpers ---------------- */
const norm = (v) => (v == null ? "" : String(v));
const trim = (v) => norm(v).trim();
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));
const pad2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const todayYMD = () => {
  try {
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Sao_Paulo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(new Date());
  } catch {
    return ymd(new Date());
  }
};

const toBR = (ymdValue) => {
  if (!ymdValue || typeof ymdValue !== "string") return "";
  const [y, m, d] = ymdValue.split("-");
  if (!y || !m || !d) return "";
  return `${d}/${m}`;
};

function timeAgo(ts) {
  const now = Date.now();
  const diff = Math.max(0, now - ts);
  const min = Math.floor(diff / 60000);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (diff < 15000) return "agora";
  if (min < 60) return `há ${min} min`;
  if (hr < 24) return `há ${hr} h`;
  return `há ${day} d`;
}

function formatHM(totalSeconds) {
  const sec = Number(totalSeconds) || 0;
  const h = Math.floor(sec / 3600);
  const m = Math.round((sec % 3600) / 60);
  if (!h && !m) return "0h";
  if (!h) return `${m}m`;
  if (!m) return `${h}h`;
  return `${h}h ${m}m`;
}

function getPctTone(pct, { inverse = false } = {}) {
  const n = clamp(Number(pct) || 0, 0, 100);
  if (!inverse) {
    if (n >= 90) return "good";
    if (n >= 75) return "midhigh";
    if (n >= 60) return "mid";
    if (n >= 45) return "midlow";
    return "bad";
  }
  if (n <= 10) return "good";
  if (n <= 20) return "midhigh";
  if (n <= 30) return "mid";
  if (n <= 40) return "midlow";
  return "bad";
}

function HalfGauge({ pct = 0, tone = "good", caption, targetPct = 90 }) {
  const safePct = clamp(Number(pct) || 0, 0, 100);
  const safeTarget = clamp(Number(targetPct) || 0, 0, 100);
  const angle = Math.PI - (safeTarget / 100) * Math.PI;
  const cx = 46;
  const cy = 46;
  const r = 36;
  const tx = cx + r * Math.cos(angle);
  const ty = cy - r * Math.sin(angle);
  const ux = (tx - cx) / r;
  const uy = (ty - cy) / r;
  const labelX = tx + ux * 7 + 6;
  const labelY = ty + uy * 7 - 1;
  return (
    <div className={`home-half-gauge tone-${tone}`} aria-hidden="true">
      <svg viewBox="0 0 92 52" className="home-half-gauge__svg">
        <path
          d="M 10 46 A 36 36 0 0 1 82 46"
          pathLength="100"
          className="home-half-gauge__track"
        />
        <path
          d="M 10 46 A 36 36 0 0 1 82 46"
          pathLength="100"
          className="home-half-gauge__progress"
          style={{ strokeDasharray: `${safePct} 100` }}
        />
        <line x1={tx - 3.4} y1={ty + 2.8} x2={tx + 2.6} y2={ty - 3.2} className="home-half-gauge__target" />
        <circle cx={tx} cy={ty} r="1.9" className="home-half-gauge__target-dot" />
        <text x={labelX} y={labelY} textAnchor="middle" className="home-half-gauge__target-label">
          {safeTarget}%
        </text>
      </svg>
      <div className="home-half-gauge__value">{safePct}%</div>
      {caption ? <div className="home-half-gauge__caption">{caption}</div> : null}
    </div>
  );
}

const HOME_GERENCIAS_FIXAS = [
  "UP NORTE",
  "UP CENTRO NORTE",
  "UP CENTRO",
  "UP PORTOS",
  "UP SERRAS",
  "UP RS",
];

function makeEmptyGerenciaStatus(gerencia) {
  return {
    gerencia,
    programados: 0,
    finalizados: 0,
    reprogramados: 0,
    abertos: 0,
    tempoPlanejadoS: 0,
    tempoExecutadoS: 0,
    quantidadePlanejada: 0,
    quantidadeExecutada: 0,
    atividadesComExpectativa: 0,
    intervalosPrimeiroIniciados: 0,
    intervalosSegundoIniciados: 0,
    intervalosTerceiroIniciados: 0,
    intervalosPrimeiroAbertos: 0,
    intervalosSegundoAbertos: 0,
    intervalosTerceiroAbertos: 0,
    intervalosIniciadosTotal: 0,
    intervalosAbertosTotal: 0,
    intervalosEmAndamentoComparacaoTotal: 0,
    pctFinalizados: 0,
    pctReprogramados: 0,
    pctAbertos: 0,
    pctTempo: 0,
    pctQuantidade: 0,
    pctExpectativa: 0,
    pctIntervalosAbertos: 0,
    atividadesProgramadas: [],
    atividadesProgramadasTipos: 0,
  };
}

function normalizeGerenciaLabel(value) {
  const raw = trim(value).toUpperCase().replace(/\s+/g, " ");
  if (!raw) return "";
  if (raw === "UP PORTO") return "UP PORTOS";
  return raw;
}

const EMPTY_RD_CHARTS = {
  prefix: { d: 0, e: 0, s: 0, total: 0, pctD: 0, pctE: 0, pctS: 0 },
  conclusao: {
    pct: 0,
    finalizados: 0,
    reprogramados: 0,
    abertos: 0,
    programados: 0,
    pctFinalizados: 0,
    pctReprogramados: 0,
    pctAbertos: 0,
  },
  tempo: { pct: 0, planejadoS: 0, executadoS: 0 },
  quantidade: { pct: 0, planejado: 0, executado: 0 },
  trend: [],
  expectativa: {
    atividadesComExpectativa: 0,
    intervalosPrimeiroIniciados: 0,
    intervalosSegundoIniciados: 0,
    intervalosTerceiroIniciados: 0,
    intervalosPrimeiroAbertos: 0,
    intervalosSegundoAbertos: 0,
    intervalosTerceiroAbertos: 0,
    intervalosIniciadosTotal: 0,
    intervalosAbertosTotal: 0,
  },
  gerenciaStatus: HOME_GERENCIAS_FIXAS.map(makeEmptyGerenciaStatus),
};

const LS_RECENTS_KEY = "pai4_home_recents_v11";
function loadRecents() {
  try {
    const raw = localStorage.getItem(LS_RECENTS_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(arr)) return [];
    return arr.filter((x) => x && x.id && x.label && x.ts).slice(0, 12);
  } catch {
    return [];
  }
}
function saveRecents(list) {
  try {
    localStorage.setItem(LS_RECENTS_KEY, JSON.stringify(list.slice(0, 12)));
  } catch {}
}

/* -------- BR Holidays (nacionais) -------- */
function easterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function brHolidaysMap(year) {
  const map = new Map();
  const addFixed = (mm, dd, name) => map.set(`${year}-${pad2(mm)}-${pad2(dd)}`, name);
  const addDate = (d, name) => map.set(ymd(d), name);

  addFixed(1, 1, "Confraternização Universal");
  addFixed(4, 21, "Tiradentes");
  addFixed(5, 1, "Dia do Trabalhador");
  addFixed(9, 7, "Independência do Brasil");
  addFixed(10, 12, "Nossa Senhora Aparecida");
  addFixed(11, 2, "Finados");
  addFixed(11, 15, "Proclamação da República");
  addFixed(12, 25, "Natal");

  const easter = easterDate(year);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  const carnavalMon = new Date(easter);
  carnavalMon.setDate(easter.getDate() - 48);
  const carnavalTue = new Date(easter);
  carnavalTue.setDate(easter.getDate() - 47);
  const corpus = new Date(easter);
  corpus.setDate(easter.getDate() + 60);

  addDate(goodFriday, "Sexta-feira Santa");
  addDate(carnavalMon, "Carnaval");
  addDate(carnavalTue, "Carnaval");
  addDate(corpus, "Corpus Christi");

  return map;
}

/* -------- Mini Calendar (Dom...Sáb) -------- */
function monthLabelPtBR(date) {
  try {
    const s = new Intl.DateTimeFormat("pt-BR", { month: "long", year: "numeric" }).format(date);
    return s.charAt(0).toUpperCase() + s.slice(1);
  } catch {
    return `${date.getMonth() + 1}/${date.getFullYear()}`;
  }
}

function buildMonthGrid(viewDate) {
  const y = viewDate.getFullYear();
  const m = viewDate.getMonth();

  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);

  // Domingo primeiro (JS: dom=0 ... sáb=6)
  const firstCol = first.getDay();
  const daysInMonth = last.getDate();

  const cells = [];
  const prevLast = new Date(y, m, 0).getDate();

  for (let i = 0; i < firstCol; i++) {
    const dayNum = prevLast - (firstCol - 1 - i);
    cells.push({ day: dayNum, inMonth: false, date: new Date(y, m - 1, dayNum) });
  }

  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, inMonth: true, date: new Date(y, m, d) });
  }

  while (cells.length < 42) {
    const nextDay = cells.length - (firstCol + daysInMonth) + 1;
    cells.push({ day: nextDay, inMonth: false, date: new Date(y, m + 1, nextDay) });
  }

  const weeks = [];
  for (let i = 0; i < 6; i++) weeks.push(cells.slice(i * 7, i * 7 + 7));
  return weeks;
}

function MiniCalendar() {
  const today = new Date();
  const [view, setView] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const weeks = useMemo(() => buildMonthGrid(view), [view]);
  const label = useMemo(() => monthLabelPtBR(view), [view]);
  const holidays = useMemo(() => brHolidaysMap(view.getFullYear()), [view]);

  const isToday = (d) =>
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();

  const dow = useMemo(() => ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"], []);

  return (
    <div className="cal" aria-label="Calendário mensal">
      <div className="cal__top">
        <button
          type="button"
          className="cal__nav"
          aria-label="Mês anterior"
          onClick={() => setView((p) => new Date(p.getFullYear(), p.getMonth() - 1, 1))}
        >
          ‹
        </button>
        <div className="cal__label">{label}</div>
        <button
          type="button"
          className="cal__nav"
          aria-label="Próximo mês"
          onClick={() => setView((p) => new Date(p.getFullYear(), p.getMonth() + 1, 1))}
        >
          ›
        </button>
      </div>

      <div className="cal__dow" aria-hidden="true">
        {dow.map((x, i) => (
          <div
            key={i}
            className={[
              "cal__dowItem",
              i === 0 ? "is-sun" : "",
              i === 6 ? "is-sat" : "",
            ].join(" ")}
          >
            {x}
          </div>
        ))}
      </div>

      <div className="cal__grid" role="grid" aria-label="Dias do mês">
        {weeks.flat().map((c, idx) => {
          const col = idx % 7; // 0=dom ... 6=sáb
          const key = ymd(c.date);
          const holidayName = holidays.get(key);

          return (
            <div
              key={idx}
              role="gridcell"
              className={[
                "cal__cell",
                c.inMonth ? "is-inMonth" : "is-outMonth",
                col === 0 ? "is-sun" : "",
                col === 6 ? "is-sat" : "",
                holidayName ? "is-holiday" : "",
                isToday(c.date) ? "is-today" : "",
              ].join(" ")}
              title={holidayName || ""}
              aria-label={holidayName ? `${c.day} - ${holidayName}` : `${c.day}`}
            >
              {c.day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===========================
   Home
=========================== */
export default function Home({
  onOpenPlanejamento,
  onOpenPlanejamentoEdicao,
  onOpenPlanejamentoMensal,
  onOpenPlanejamentoMensalAjuste,
  onOpenPlanejamentoMensalContrato,
  onOpenAtividades,
  onOpenDesvios,

  // ? NOVO: Desvios (Edição)
  onOpenDesviosEdicao,
  onOpenDesviosNegar,

  onOpenEmails,
  onOpenAutomacaoEmails,
  onOpenAtivos,
  onOpenEquipamentos,
  onOpenSboe,
  onOpenReabertura,
  onOpenD1Calendario,
  onOpenExecucaoDia,
  onOpenStatusDiario,
  onOpenRelatorioDiario,

  // ? NOVO
  onOpenProgramacaoDmais1,
  onOpenCimInterdicoes,
  onOpenCimStatus,
  onOpenCimInterdicoesCco,
  onOpenSimuladorWorkbench,
  onOpenCadastroTrilhosNovos,
  onOpenCadastroTrilhosReemprego,
  onOpenDepositoTrilhos,
  onOpenManutencaoEstaleiro,
  onOpenManutencaoCim,
}) {
  const { user, logout } = useAuth();

  const rawName = user?.name || user?.preferred_username || "Usuário";
  const shortName = useMemo(() => {
    const s = trim(rawName);
    const parts = s.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0]} ${parts[1]}`;
    return parts[0] || "Usuário";
  }, [rawName]);

  const userMail = user?.preferred_username || "";

  useEffect(() => {
    const prevHtmlOv = document.documentElement.style.overflow;
    const prevBodyOv = document.body.style.overflow;
    const prevBg = document.body.style.background;
    const prevMar = document.body.style.margin;

    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.background = "var(--home-bg)";
    document.body.style.margin = "0";

    return () => {
      document.documentElement.style.overflow = prevHtmlOv;
      document.body.style.overflow = prevBodyOv;
      document.body.style.background = prevBg;
      document.body.style.margin = prevMar;
    };
  }, []);

  const openAutomacao = onOpenAutomacaoEmails || onOpenEmails || (() => {});
  const openEquipamentos = onOpenEquipamentos || (() => {});
  const openSboe = onOpenSboe || (() => {});
  const openPlanejamento = onOpenPlanejamento || (() => {});
  const openPlanejamentoEd = onOpenPlanejamentoEdicao || (() => {});
  const openPlanejamentoMensal = onOpenPlanejamentoMensal || (() => {});
  const openPlanejamentoMensalAjuste = onOpenPlanejamentoMensalAjuste || (() => {});
  const openPlanejamentoMensalContrato = onOpenPlanejamentoMensalContrato || (() => {});
  const openReabertura = onOpenReabertura || (() => {});
  const openD1 = onOpenD1Calendario || (() => {});
  const openExecDia = onOpenExecucaoDia || (() => {});
  const openStatusDiario = onOpenStatusDiario || (() => {});
  const openRelatorioDiario = onOpenRelatorioDiario || (() => {});

  // ? NOVO
  const openDmais1 = onOpenProgramacaoDmais1 || (() => {});
  const openCimInterdicoes = onOpenCimInterdicoes || (() => {});
  const openCimStatus = onOpenCimStatus || (() => {});
  const openCimInterdicoesCco = onOpenCimInterdicoesCco || (() => {});
  const openSimuladorWorkbench = onOpenSimuladorWorkbench || (() => {});
  const openCadastroTrilhosNovos = onOpenCadastroTrilhosNovos || (() => {});
  const openCadastroTrilhosReemprego = onOpenCadastroTrilhosReemprego || (() => {});
  const openDepositoTrilhos = onOpenDepositoTrilhos || (() => {});
  const openManutencaoEstaleiro = onOpenManutencaoEstaleiro || (() => {});
  const openManutencaoCim = onOpenManutencaoCim || (() => {});

  // ? NOVO: Desvios (Edição)
  const openDesviosEdicao = onOpenDesviosEdicao || (() => {});
  const openDesviosNegar = onOpenDesviosNegar || (() => {});

  const [q, setQ] = useState("");
  const [recents, setRecents] = useState(() => loadRecents());
  const [rdSummary, setRdSummary] = useState(null);
  const [rdLoading, setRdLoading] = useState(true);
  const [rdHasLoadedOnce, setRdHasLoadedOnce] = useState(false);
  const [rdCharts, setRdCharts] = useState(EMPTY_RD_CHARTS);

  useEffect(() => {
    let alive = true;
    let pending = false;
    let controller = null;

    const load = () => {
      if (pending) return;
      pending = true;
      controller?.abort?.();
      controller = new AbortController();
      setRdLoading(true);

      getRelatorioDiario({ day: todayYMD(), signal: controller.signal })
        .then((data) => {
          if (!alive) return;
          const rows = Array.isArray(data?.rows) ? data.rows : [];
          const last = rows.length ? rows[rows.length - 1] : null;
          setRdSummary({
            data: last?.data || data?.end || "",
            programados: Number(last?.programados) || 0,
            finalizados: Number(last?.finalizados) || 0,
            reprogramados: Number(last?.reprogramados) || 0,
            abertos: Number(last?.abertos) || 0,
            prefixoE: Number(last?.prefixo_e) || 0,
          });
          const prefixRows = Array.isArray(data?.prefix) ? data.prefix : [];
          const prefixLast = prefixRows.length ? prefixRows[prefixRows.length - 1] : null;
          const prefixD = Number(prefixLast?.prefixo_d) || 0;
          const prefixE = Number(prefixLast?.prefixo_e) || 0;
          const prefixS = Number(prefixLast?.prefixo_semanal) || 0;
          const prefixTotal = prefixD + prefixE + prefixS;

          const atividades = Array.isArray(data?.atividades) ? data.atividades : [];
          const tempoPlanejadoS = atividades.reduce((acc, a) => acc + (Number(a?.tempo_planejado_s) || 0), 0);
          const tempoExecutadoS = atividades.reduce((acc, a) => acc + (Number(a?.tempo_executado_s) || 0), 0);
          const qtdPlanejada = atividades.reduce((acc, a) => acc + (Number(a?.quantidade_planejada) || 0), 0);
          const qtdExecutada = atividades.reduce((acc, a) => acc + (Number(a?.quantidade_executada) || 0), 0);

          const baseConclusao = Number(last?.programados) || 0;
          const finalizados = Number(last?.finalizados) || 0;
          const reprogramados = Number(last?.reprogramados) || 0;
          const abertos = Number(last?.abertos) || 0;
          const taxaConclusao = baseConclusao ? Math.round((finalizados / baseConclusao) * 100) : 0;
          const taxaReprogramados = baseConclusao ? Math.round((reprogramados / baseConclusao) * 100) : 0;
          const taxaAbertos = baseConclusao ? Math.round((abertos / baseConclusao) * 100) : 0;
          const tempoPct = tempoPlanejadoS ? Math.round((tempoExecutadoS / tempoPlanejadoS) * 100) : 0;
          const qtdPct = qtdPlanejada ? Math.round((qtdExecutada / qtdPlanejada) * 100) : 0;

          const trendRows = Array.isArray(data?.trendRows) ? data.trendRows : [];
          const trend = trendRows.map((r) => {
            const tempoPlan = Number(r?.tempo_planejado_s) || 0;
            const tempoExec = Number(r?.tempo_executado_s) || 0;
            const qtdPlan = Number(r?.quantidade_planejada) || 0;
            const qtdExec = Number(r?.quantidade_executada) || 0;
            const tempoPct = tempoPlan ? Math.round((tempoExec / tempoPlan) * 100) : 0;
            const quantidadePct = qtdPlan ? Math.round((qtdExec / qtdPlan) * 100) : 0;
            return {
              date: r?.data || "",
              tempoPct,
              quantidadePct,
            };
          });

          const expectativaRaw = data?.expectativa || {};
          const expectativa = {
            atividadesComExpectativa: Number(expectativaRaw?.atividades_com_expectativa) || 0,
            intervalosPrimeiroIniciados: Number(expectativaRaw?.intervalos_primeiro_iniciados) || 0,
            intervalosSegundoIniciados: Number(expectativaRaw?.intervalos_segundo_iniciados) || 0,
            intervalosTerceiroIniciados: Number(expectativaRaw?.intervalos_terceiro_iniciados) || 0,
            intervalosPrimeiroAbertos: Number(expectativaRaw?.intervalos_primeiro_abertos) || 0,
            intervalosSegundoAbertos: Number(expectativaRaw?.intervalos_segundo_abertos) || 0,
            intervalosTerceiroAbertos: Number(expectativaRaw?.intervalos_terceiro_abertos) || 0,
          };
          expectativa.intervalosIniciadosTotal =
            expectativa.intervalosPrimeiroIniciados +
            expectativa.intervalosSegundoIniciados +
            expectativa.intervalosTerceiroIniciados;
          expectativa.intervalosAbertosTotal =
            expectativa.intervalosPrimeiroAbertos +
            expectativa.intervalosSegundoAbertos +
            expectativa.intervalosTerceiroAbertos;

          const gerenciasResumoRaw = Array.isArray(data?.gerenciasResumo) ? data.gerenciasResumo : [];
          const gerenciasAtividadesRaw = Array.isArray(data?.gerenciasAtividades) ? data.gerenciasAtividades : [];
          const gerenciaBaseMap = new Map();
          const gerenciaAtividadesMap = new Map();

          for (const g of gerenciasResumoRaw) {
            const gerencia = normalizeGerenciaLabel(g?.gerencia);
            if (!gerencia) continue;

            const current = gerenciaBaseMap.get(gerencia) || {
              gerencia,
              programados: 0,
              finalizados: 0,
              reprogramados: 0,
              abertos: 0,
              tempoPlanejadoS: 0,
              tempoExecutadoS: 0,
              quantidadePlanejada: 0,
              quantidadeExecutada: 0,
              atividadesComExpectativa: 0,
              intervalosPrimeiroIniciados: 0,
              intervalosSegundoIniciados: 0,
              intervalosTerceiroIniciados: 0,
              intervalosPrimeiroAbertos: 0,
              intervalosSegundoAbertos: 0,
              intervalosTerceiroAbertos: 0,
            };

            current.programados += Number(g?.programados) || 0;
            current.finalizados += Number(g?.finalizados) || 0;
            current.reprogramados += Number(g?.reprogramados) || 0;
            current.abertos += Number(g?.abertos) || 0;
            current.tempoPlanejadoS += Number(g?.tempo_planejado_s) || 0;
            current.tempoExecutadoS += Number(g?.tempo_executado_s) || 0;
            current.quantidadePlanejada += Number(g?.quantidade_planejada) || 0;
            current.quantidadeExecutada += Number(g?.quantidade_executada) || 0;
            current.atividadesComExpectativa += Number(g?.atividades_com_expectativa) || 0;
            current.intervalosPrimeiroIniciados += Number(g?.intervalos_primeiro_iniciados) || 0;
            current.intervalosSegundoIniciados += Number(g?.intervalos_segundo_iniciados) || 0;
            current.intervalosTerceiroIniciados += Number(g?.intervalos_terceiro_iniciados) || 0;
            current.intervalosPrimeiroAbertos += Number(g?.intervalos_primeiro_abertos) || 0;
            current.intervalosSegundoAbertos += Number(g?.intervalos_segundo_abertos) || 0;
            current.intervalosTerceiroAbertos += Number(g?.intervalos_terceiro_abertos) || 0;

            gerenciaBaseMap.set(gerencia, current);
          }

          for (const row of gerenciasAtividadesRaw) {
            const gerencia = normalizeGerenciaLabel(row?.gerencia);
            const atividade = trim(row?.atividade);
            if (!gerencia || !atividade) continue;
            const qtd = Number(row?.programadas) || 0;
            if (!qtd) continue;

            if (!gerenciaAtividadesMap.has(gerencia)) {
              gerenciaAtividadesMap.set(gerencia, new Map());
            }
            const actMap = gerenciaAtividadesMap.get(gerencia);
            actMap.set(atividade, (actMap.get(atividade) || 0) + qtd);
          }

          const gerenciaStatus = HOME_GERENCIAS_FIXAS.map((gerencia) => {
            const baseRow = gerenciaBaseMap.get(gerencia) || {
              gerencia,
              programados: 0,
              finalizados: 0,
              reprogramados: 0,
              abertos: 0,
              tempoPlanejadoS: 0,
              tempoExecutadoS: 0,
              quantidadePlanejada: 0,
              quantidadeExecutada: 0,
              atividadesComExpectativa: 0,
              intervalosPrimeiroIniciados: 0,
              intervalosSegundoIniciados: 0,
              intervalosTerceiroIniciados: 0,
              intervalosPrimeiroAbertos: 0,
              intervalosSegundoAbertos: 0,
              intervalosTerceiroAbertos: 0,
            };
            const atividadesMap = gerenciaAtividadesMap.get(gerencia);
            const atividadesProgramadas = atividadesMap
              ? Array.from(atividadesMap.entries())
                  .map(([atividade, programadas]) => ({ atividade, programadas }))
                  .sort((a, b) => (b.programadas - a.programadas) || a.atividade.localeCompare(b.atividade, "pt-BR"))
              : [];

            const base = baseRow.programados || (baseRow.finalizados + baseRow.reprogramados + baseRow.abertos);
            const intervalosIniciadosTotal =
              baseRow.intervalosPrimeiroIniciados +
              baseRow.intervalosSegundoIniciados +
              baseRow.intervalosTerceiroIniciados;
            const intervalosAbertosTotal =
              baseRow.intervalosPrimeiroAbertos +
              baseRow.intervalosSegundoAbertos +
              baseRow.intervalosTerceiroAbertos;
            return {
              ...baseRow,
              intervalosIniciadosTotal,
              intervalosAbertosTotal,
              intervalosEmAndamentoComparacaoTotal: Number(baseRow.abertos) || 0,
              pctFinalizados: base ? Math.round((baseRow.finalizados / base) * 100) : 0,
              pctReprogramados: base ? Math.round((baseRow.reprogramados / base) * 100) : 0,
              pctAbertos: base ? Math.round((baseRow.abertos / base) * 100) : 0,
              pctTempo: baseRow.tempoPlanejadoS ? Math.round((baseRow.tempoExecutadoS / baseRow.tempoPlanejadoS) * 100) : 0,
              pctQuantidade: baseRow.quantidadePlanejada ? Math.round((baseRow.quantidadeExecutada / baseRow.quantidadePlanejada) * 100) : 0,
              pctExpectativa: baseRow.programados ? Math.round((baseRow.atividadesComExpectativa / baseRow.programados) * 100) : 0,
              pctIntervalosAbertos: (Number(baseRow.abertos) || 0)
                ? Math.round((Math.min(intervalosAbertosTotal, Number(baseRow.abertos) || 0) / (Number(baseRow.abertos) || 1)) * 100)
                : 0,
              atividadesProgramadas,
              atividadesProgramadasTipos: atividadesProgramadas.length,
            };
          });

          setRdCharts({
            prefix: {
              d: prefixD,
              e: prefixE,
              s: prefixS,
              total: prefixTotal,
              pctD: prefixTotal ? Math.round((prefixD / prefixTotal) * 100) : 0,
              pctE: prefixTotal ? Math.round((prefixE / prefixTotal) * 100) : 0,
              pctS: prefixTotal ? Math.round((prefixS / prefixTotal) * 100) : 0,
            },
            conclusao: {
              pct: Math.min(100, Math.max(0, taxaConclusao)),
              finalizados,
              reprogramados,
              abertos,
              programados: baseConclusao,
              pctFinalizados: Math.min(100, Math.max(0, taxaConclusao)),
              pctReprogramados: Math.min(100, Math.max(0, taxaReprogramados)),
              pctAbertos: Math.min(100, Math.max(0, taxaAbertos)),
            },
            tempo: {
              pct: Math.min(100, Math.max(0, tempoPct)),
              planejadoS: tempoPlanejadoS,
              executadoS: tempoExecutadoS,
            },
            quantidade: {
              pct: Math.min(100, Math.max(0, qtdPct)),
              planejado: qtdPlanejada,
              executado: qtdExecutada,
            },
            trend,
            expectativa,
            gerenciaStatus,
          });
        })
        .catch(() => {
          if (!alive) return;
          // Mantem o ultimo snapshot para evitar "piscar vazio" no painel.
        })
        .finally(() => {
          if (!alive) return;
          pending = false;
          setRdLoading(false);
          setRdHasLoadedOnce(true);
        });
    };

    load();
    const interval = setInterval(load, 30000);

    return () => {
      alive = false;
      controller?.abort?.();
      clearInterval(interval);
    };
  }, []);

  const pushRecent = useCallback((item) => {
    const now = Date.now();
    setRecents((prev) => {
      const filtered = prev.filter((x) => x.id !== item.id);
      const next = [{ id: item.id, label: item.label, icon: item.icon, ts: now }, ...filtered].slice(0, 6);
      saveRecents(next);
      return next;
    });
  }, []);

  const runItem = useCallback(
    (item) => {
      if (item?.disabled) return;
      item.onClick?.();
      pushRecent(item);
    },
    [pushRecent]
  );

  const programacoes = useMemo(
    () => [
      { id: "planejamento", label: "Planejamento Integrado", desc: "Criação do plano macro.", icon: "🗓️", group: "Programações", onClick: openPlanejamento },
      { id: "planejamento_mensal", label: "Planejamento Mensal", desc: "Planejamento mensal da operação.", icon: "📆", group: "Programações", onClick: openPlanejamentoMensal },
      { id: "ajuste_mensal", label: "Ajuste Mensal", desc: "Ajustes sobre os dados salvos no planejamento mensal.", icon: "🧮", group: "Programações", onClick: openPlanejamentoMensalAjuste },
      { id: "contrato_mensal", label: "Aprovação do Contrato Mensal", desc: "Etapa final de aprovação do fluxo mensal.", icon: "✅", group: "Programações", onClick: openPlanejamentoMensalContrato },
      { id: "planejamento_ed", label: "Gestão do Planejamento", desc: "Ajustes e revisões do plano operacional.", icon: "✏️", group: "Programações", onClick: openPlanejamentoEd },
      { id: "d1", label: "Programação Operacional D+1", desc: "Validação da programação do próximo dia em visão de calendário.", icon: "🗓️", group: "Programações", onClick: openD1 },
      { id: "exec_dia", label: "Execução Operacional Diária", desc: "Acompanhamento e registro das execuções do dia.", icon: "🕒", group: "Programações", onClick: openExecDia },
    ],
    [
      openPlanejamento,
      openPlanejamentoMensal,
      openPlanejamentoMensalAjuste,
      openPlanejamentoMensalContrato,
      openPlanejamentoEd,
      openD1,
      openExecDia,
    ]
  );

  const manutencao = useMemo(
    () => [
      { id: "ativos", label: "Catálogo de Ativos", desc: "Cadastros e padronização.", icon: "🧩", group: "Manutenção", onClick: onOpenAtivos || (() => {}) },
      { id: "equip", label: "Catálogo de Equipamentos", desc: "Trechos, KP e metadados.", icon: "🛠️", group: "Manutenção", onClick: openEquipamentos },
      { id: "sboe", label: "SB Origem e Encerramento", desc: "Mapeamento e manutenção de pares SUB/SB.", icon: "🔗", group: "Manutenção", onClick: openSboe },
      { id: "atividades", label: "Catálogo de Atividades", desc: "Catálogo e regras.", icon: "📋", group: "Manutenção", onClick: onOpenAtividades || (() => {}) },
      { id: "desvios", label: "Catálogo de Desvios - Fechamento", desc: "Desvios utilizados na etapa de finalização e fechamento.", icon: "🚧", group: "Manutenção", onClick: onOpenDesvios || (() => {}) },

      // ? NOVO: Desvios (Edição)
      { id: "desvios_edicao", label: "Catálogo de Desvios - Edição", desc: "Manutenção da base de desvios para uso operacional.", icon: "✏️", group: "Manutenção", onClick: openDesviosEdicao },

      { id: "desvios_cancelamento", label: "Catálogo de Desvios - Cancelamento", desc: "Manutenção da base de desvios para cenários de negativa/cancelamento.", icon: "🧷", group: "Manutenção", onClick: openDesviosNegar },
      { id: "emails", label: "Catálogo de E-mails", desc: "Modelos e ajustes.", icon: "✉️", group: "Manutenção", onClick: onOpenEmails || (() => {}) },
      { id: "permissoes", label: "Permissões", desc: "Perfis e acessos (em breve).", icon: "🔐", group: "Manutenção", onClick: () => {} },
    ],
    [onOpenAtivos, openEquipamentos, openSboe, onOpenAtividades, onOpenDesvios, onOpenEmails, openDesviosEdicao, openDesviosNegar]
  );

  const utilitarios = useMemo(
    () => [
      { id: "status", label: "Status Diário", desc: "Indicadores e consolidação.", icon: "📈", group: "Utilitários", onClick: openStatusDiario },
      { id: "relatorio_diario", label: "Relatório Diário", desc: "Análises e gráficos do dia.", icon: "📒", group: "Utilitários", onClick: openRelatorioDiario },

      // ? NOVO
      { id: "dmais1", label: "Programação D+1", desc: "Pré-visualização do próximo dia.", icon: "⏭️", group: "Utilitários", onClick: openDmais1 },

      { id: "auto_emails", label: "Automação de E-mails", desc: "Disparos e regras.", icon: "🤖", group: "Utilitários", onClick: openAutomacao },
      { id: "export_csv", label: "Exportação CSV", desc: "Exportações rápidas (em breve).", icon: "📤", group: "Utilitários", onClick: () => {} },
      { id: "import_base", label: "Importação de Base", desc: "Carga assistida (em breve).", icon: "📥", group: "Utilitários", onClick: () => {} },
      { id: "logs", label: "Logs e Auditoria", desc: "Eventos e auditoria (em breve).", icon: "🧾", group: "Utilitários", onClick: () => {} },
    ],
    [openStatusDiario, openRelatorioDiario, openDmais1, openAutomacao]
  );

  const simuladores = useMemo(
    () => [
      {
        id: "simulador_workbench",
        label: "Workbench de Simulação",
        desc: "Simulador de capacidade/produção x plano (mock).",
        icon: "🧪",
        group: "Simuladores",
        onClick: openSimuladorWorkbench,
      },
    ],
    [openSimuladorWorkbench]
  );

  const estaleiro = useMemo(
    () => [
      {
        id: "cadastro_trilhos_novos",
        label: "Cadastro de Trilhos Novos",
        desc: "Ficha simples para cadastro de trilhos.",
        icon: "🛤️",
        group: "Estaleiro",
        onClick: openCadastroTrilhosNovos,
      },
      {
        id: "cadastro_trilhos_reemprego",
        label: "Cadastro de Trilhos Reemprego",
        desc: "Ficha de romaneio para trilhos de reemprego.",
        icon: "📄",
        group: "Estaleiro",
        onClick: openCadastroTrilhosReemprego,
      },
      {
        id: "deposito_trilhos",
        label: "Depósito de Trilhos",
        desc: "Consolidado do que foi salvo em Novos e Reemprego.",
        icon: "🏗️",
        group: "Estaleiro",
        onClick: openDepositoTrilhos,
      },
      {
        id: "manutencao_estaleiro",
        label: "Manutenção Estaleiro",
        desc: "Gestão das bases do estaleiro (perfil, origem, marca e demais cadastros).",
        icon: "🧰",
        group: "Estaleiro",
        onClick: openManutencaoEstaleiro,
      },
    ],
    [openCadastroTrilhosNovos, openCadastroTrilhosReemprego, openDepositoTrilhos, openManutencaoEstaleiro]
  );

  const intervencoes = useMemo(
    () => [{ id: "reabertura", label: "Reabertura de Programações", desc: "Ações controladas.", icon: "♻️", group: "Intervenções", onClick: openReabertura }],
    [openReabertura]
  );

  const cim = useMemo(
    () => [
      {
        id: "cim_interdicoes",
        label: "Interdições",
        desc: "Cadastro visual e acompanhamento das interdições.",
        icon: "⛔",
        group: "CIM",
        onClick: openCimInterdicoes,
      },
      {
        id: "cim_status",
        label: "Status CIM",
        desc: "Painel diário das interdições do CIM com foco operacional.",
        icon: "📋",
        group: "CIM",
        onClick: openCimStatus,
      },
      {
        id: "cim_interdicoes_cco",
        label: "Interdições CCO",
        desc: "Cards operacionais para lançar a numeração das interdições.",
        icon: "🔢",
        group: "CIM",
        onClick: openCimInterdicoesCco,
      },
      {
        id: "cim_manutencao",
        label: "Manutenção CIM",
        desc: "Central das bases do CIM para apoiar interdições e cadastros futuros.",
        icon: "🧰",
        group: "CIM",
        onClick: openManutencaoCim,
      },
    ],
    [openCimInterdicoes, openCimStatus, openCimInterdicoesCco, openManutencaoCim]
  );

  const allItems = useMemo(() => [...programacoes, ...manutencao, ...utilitarios, ...simuladores, ...estaleiro, ...intervencoes, ...cim], [
    programacoes, manutencao, utilitarios, simuladores, estaleiro, intervencoes, cim,
  ]);

  const query = trim(q).toLowerCase();
  const match = useCallback(
    (it) => {
      if (!query) return true;
      return (
        it.label.toLowerCase().includes(query) ||
        (it.desc || "").toLowerCase().includes(query) ||
        (it.group || "").toLowerCase().includes(query)
      );
    },
    [query]
  );

  const todayLabel = useMemo(() => {
    try {
      const formatter = new Intl.DateTimeFormat("pt-BR", {
        weekday: "long",
        day: "2-digit",
        month: "long",
      });
      const value = formatter.format(new Date());
      return value.charAt(0).toUpperCase() + value.slice(1);
    } catch {
      return "";
    }
  }, []);

    const dashCards = useMemo(() => {
    const cards = [
      { id: "painel_fila", title: "Fila do dia", desc: "Lista priorizada do que está programado/rodando.", icon: "📌" },
      { id: "painel_alertas", title: "Alertas críticos", desc: "Bloqueios, atrasos e conflitos de km/SB.", icon: "🚨" },
      { id: "painel_pendencias", title: "Pendências WF-3", desc: "Executado sem fechamento/justificativa.", icon: "🧾" },
      { id: "painel_prod", title: "Produtividade", desc: "Planejado x realizado com tendência.", icon: "📊" },
      { id: "painel_saude", title: "Saúde do workflow", desc: "Inconsistências e status fora do padrão.", icon: "🛡️" },
    ];
    if (!query) return cards;
    return cards.filter((c) => (c.title + " " + c.desc).toLowerCase().includes(query));
  }, [query]);

  
  const summaryStats = useMemo(
    () => [
      { label: "Total de módulos", value: allItems.length },
      { label: "Programações", value: programacoes.length },
      { label: "Manutenção", value: manutencao.length },
      { label: "Utilitários", value: utilitarios.length },
      { label: "Simuladores", value: simuladores.length },
      { label: "Estaleiro", value: estaleiro.length },
      { label: "Intervenções", value: intervencoes.length },
      { label: "CIM", value: cim.length },
    ],
    [allItems.length, programacoes.length, manutencao.length, utilitarios.length, simuladores.length, estaleiro.length, intervencoes.length, cim.length]
  );

  const sidebarGroups = useMemo(
    () => [
      { id: "prog", title: "Programações", items: programacoes },
      { id: "manu", title: "Manutenção", items: manutencao },
      { id: "util", title: "Utilitários", items: utilitarios },
      { id: "sim", title: "Simuladores", items: simuladores },
      { id: "est", title: "Estaleiro", items: estaleiro },
      { id: "inter", title: "Intervenções", items: intervencoes },
      { id: "cim", title: "CIM", items: cim },
    ],
    [programacoes, manutencao, utilitarios, simuladores, estaleiro, intervencoes, cim]
  );

  const charts = rdCharts || EMPTY_RD_CHARTS;
  const rdFirstLoadLoading = rdLoading && !rdHasLoadedOnce;
  const gerenciaProgramadosTotal = useMemo(
    () => charts.gerenciaStatus.reduce((acc, row) => acc + (Number(row.programados) || 0), 0),
    [charts]
  );
  const statusDataLabel = rdSummary?.data
    ? `Base: ${toBR(rdSummary.data)}`
    : rdFirstLoadLoading
      ? "Carregando dados do dia..."
      : `Base: ${toBR(todayYMD())}`;

  return (
    <div className="home-shell home-shell--sidebar">
      <header className="home-topbar home-topbar--full">
        <div className="home-topbar-left">
          <h1>Olá, {shortName}</h1>
          <p>Centralize decisões e execute o plano com clareza.</p>
        </div>

        <div className="home-topbar-actions">
          <div className="search home-search" role="search">
            <span className="search__icon" aria-hidden="true">??</span>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar módulos..."
              aria-label="Buscar"
            />
            {q ? (
              <button className="search__clear" type="button" aria-label="Limpar busca" onClick={() => setQ("")}
              >
                ?
              </button>
            ) : null}
          </div>

          <div className="userbox" aria-label="Usuário">
            <button className="userball" type="button" aria-label="Conta">
              {shortName.slice(0, 1).toUpperCase()}
            </button>

            <div className="usermenu" role="menu" aria-label="Menu do usuário">
              <div className="usermenu__top">
                <div className="avatarBig" aria-hidden="true">{shortName.slice(0, 1).toUpperCase()}</div>
                <div className="usermenu__meta">
                  <div className="uname">{shortName}</div>
                  <div className="umail">{userMail}</div>
                </div>
              </div>

              <div className="usermenu__list">
                <button className="menu__item" role="menuitem" onClick={() => {}}>
                  <span className="micon">??</span>
                  <span className="mcopy">
                    <span className="mlabel">Perfil</span>
                    <span className="mdesc">Configurações pessoais (em breve)</span>
                  </span>
                </button>

                <button className="menu__item" role="menuitem" onClick={() => {}}>
                  <span className="micon">??</span>
                  <span className="mcopy">
                    <span className="mlabel">Preferências</span>
                    <span className="mdesc">Tema e atalhos (em breve)</span>
                  </span>
                </button>

                <button className="menu__item menu__item--danger" role="menuitem" onClick={logout}>
                  <span className="micon">??</span>
                  <span className="mcopy">
                    <span className="mlabel">Sair</span>
                    <span className="mdesc">Encerrar sessão</span>
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <aside className="home-sidebar">
        <div className="home-sidebar-head">
          <div className="home-sidebar-title">Menu</div>
        </div>

        <div className="home-sidebar-groups">
          {sidebarGroups.map((group) => (
            <div key={group.id} className="home-side-group">
              <div className="home-side-title">{group.title}</div>
              <div className="home-side-list">
                {group.items.filter(match).map((it) => (
                  <button
                    key={it.id}
                    className={`home-side-item ${it.disabled ? "is-disabled" : ""}`}
                    type="button"
                    onClick={() => !it.disabled && runItem(it)}
                    disabled={!!it.disabled}
                  >
                    <span className="home-side-icon" aria-hidden="true">{it.icon}</span>
                    <span className="home-side-label">{it.label}</span>
                  </button>
                ))}
                {!group.items.filter(match).length && <div className="home-empty">Nada encontrado.</div>}
              </div>
            </div>
          ))}
        </div>
      </aside>

      <div className="home-main">
        <section className="home-hero">
          <div className="home-hero-left">
            <span className="home-badge">Painel diário</span>
            <h2>Visão rápida do dia</h2>
            <p>Use o menu lateral para navegar e acompanhe os indicadores principais.</p>
            <div className="home-hero-meta">{statusDataLabel}</div>

            <div className={`home-gerencia-grid-quick ${rdFirstLoadLoading ? "is-loading" : ""}`} aria-label="Status por gerência">
              {charts.gerenciaStatus.length ? (
                charts.gerenciaStatus.map((g) => {
                  const totalDiaProgramados = charts.conclusao.programados || gerenciaProgramadosTotal;
                  const participacaoPct = totalDiaProgramados
                    ? Math.round((Number(g.programados || 0) / totalDiaProgramados) * 100)
                    : 0;
                  const baseBar = Number(g.programados) || 0;
                  const emAndamentoCount = Math.min(Number(g.intervalosAbertosTotal) || 0, Number(g.abertos) || 0);
                  const abertosSemAndamento = Math.max(0, (Number(g.abertos) || 0) - emAndamentoCount);
                  const pctAndamentoBar = baseBar ? (emAndamentoCount / baseBar) * 100 : 0;
                  const pctAbertoRestanteBar = baseBar ? (abertosSemAndamento / baseBar) * 100 : 0;
                  const pctFinalizadosBar = baseBar ? ((Number(g.finalizados) || 0) / baseBar) * 100 : 0;
                  const pctReprogramadosBar = baseBar ? ((Number(g.reprogramados) || 0) / baseBar) * 100 : 0;
                  const pctAbertoRestanteDisplay = baseBar ? Math.round((abertosSemAndamento / baseBar) * 100) : 0;
                  const conclTone = getPctTone(g.pctFinalizados);
                  const instabTone = getPctTone(g.pctReprogramados, { inverse: true });
                  const pendTone = getPctTone(g.pctAbertos, { inverse: true });
                  const tempoTone = getPctTone(g.pctTempo);
                  const qtdTone = getPctTone(g.pctQuantidade);
                  const expectativaTone = getPctTone(g.pctExpectativa);
                  const intervalosTone = getPctTone(g.pctIntervalosAbertos, { inverse: true });
                  const metaOk = (Number(g.pctFinalizados) || 0) >= 90;
                  const atividadesProgramadas = Array.isArray(g.atividadesProgramadas) ? g.atividadesProgramadas : [];
                  const atividadesRestantes = Math.max(0, (Number(g.atividadesProgramadasTipos) || 0) - atividadesProgramadas.length);

                  return (
                    <div key={g.gerencia} className={`home-gerencia-row ${rdFirstLoadLoading ? "is-loading" : ""}`}>
                      {rdFirstLoadLoading ? (
                        <div className="home-gerencia-loading" aria-hidden="true">
                          <span className="home-gerencia-spinner" />
                        </div>
                      ) : null}

                      <div className="home-gerencia-row-head">
                        <strong>{g.gerencia}</strong>
                        <span>{g.programados} programados</span>
                      </div>

                      <div className="home-gerencia-row-sub">
                        <span>{participacaoPct}% do volume do dia</span>
                        <span className={`home-badge-chip tone-${metaOk ? "good" : "midlow"}`}>
                          {metaOk ? "Meta OK" : "Abaixo da meta"}
                        </span>
                      </div>

                      <div
                        className="home-gerencia-bar"
                        style={{
                          "--a": pctAbertoRestanteBar,
                          "--and": pctAndamentoBar,
                          "--f": pctFinalizadosBar,
                          "--r": pctReprogramadosBar,
                        }}
                      />

                      <div className="home-gerencia-row-meta">
                        <span><i className="dot dot--a" /> {abertosSemAndamento} ({pctAbertoRestanteDisplay}%)</span>
                        <span><i className="dot dot--andamento" /> {emAndamentoCount} ({g.pctIntervalosAbertos}%)</span>
                        <span><i className="dot dot--f" /> {g.finalizados} ({g.pctFinalizados}%)</span>
                        <span><i className="dot dot--r" /> {g.reprogramados} ({g.pctReprogramados}%)</span>
                      </div>

                        <div className="home-gerencia-kpis">
                          <div className="home-gerencia-kpis-row home-gerencia-kpis-row--top">
                          <div className="home-gerencia-kpi">
                            <span className="home-gerencia-kpi__label">Conclusão</span>
                          <strong>{g.finalizados}/{g.programados}</strong>
                          <span className={`home-badge-chip tone-${conclTone}`}>{g.pctFinalizados}%</span>
                        </div>
                        <div className="home-gerencia-kpi">
                          <span className="home-gerencia-kpi__label">Instabilidade</span>
                          <strong>{g.reprogramados}/{g.programados}</strong>
                          <span className={`home-badge-chip tone-${instabTone}`}>{g.pctReprogramados}%</span>
                        </div>
                        <div className="home-gerencia-kpi">
                          <span className="home-gerencia-kpi__label">Pendência</span>
                          <strong>{g.abertos}/{g.programados}</strong>
                          <span className={`home-badge-chip tone-${pendTone}`}>{g.pctAbertos}%</span>
                        </div>
                          <div className="home-gerencia-kpi">
                            <span className="home-gerencia-kpi__label">Intervalos em andamento</span>
                            <strong>{emAndamentoCount} / {g.intervalosEmAndamentoComparacaoTotal}</strong>
                            <span className={`home-badge-chip tone-${intervalosTone}`}>{g.pctIntervalosAbertos}%</span>
                          </div>
                          </div>
                          <div className="home-gerencia-kpis-row home-gerencia-kpis-row--bottom">
                          <div className="home-gerencia-kpi home-gerencia-kpi--gauge">
                            <span className="home-gerencia-kpi__label">Tempo</span>
                            <HalfGauge
                              pct={g.pctTempo}
                              tone={tempoTone}
                              caption={`${formatHM(g.tempoExecutadoS)} / ${formatHM(g.tempoPlanejadoS)}`}
                            />
                          </div>
                          <div className="home-gerencia-kpi home-gerencia-kpi--gauge">
                            <span className="home-gerencia-kpi__label">Quantidade</span>
                            <HalfGauge
                              pct={g.pctQuantidade}
                              tone={qtdTone}
                              caption={`${g.quantidadeExecutada} / ${g.quantidadePlanejada}`}
                            />
                          </div>
                          <div className="home-gerencia-kpi home-gerencia-kpi--expectativa">
                            <span className="home-gerencia-kpi__label">Atividades com expectativa</span>
                            <strong>{g.atividadesComExpectativa}</strong>
                            <div className="home-inline-indicator" aria-hidden="true">
                              <div
                                className={`home-inline-indicator__fill tone-${expectativaTone}`}
                                style={{ width: `${Math.min(100, Math.max(0, g.pctExpectativa))}%` }}
                              />
                            </div>
                            <span className={`home-badge-chip tone-${expectativaTone}`}>{g.pctExpectativa}%</span>
                          </div>
                          </div>
                          <div className="home-gerencia-kpis-row home-gerencia-kpis-row--activities">
                          <div className="home-gerencia-kpi home-gerencia-kpi--atividades">
                            <span className="home-gerencia-kpi__label">Atividades programadas</span>
                            {atividadesProgramadas.length ? (
                              <>
                                <div className="home-gerencia-ativ-list" role="list" aria-label={`Atividades programadas ${g.gerencia}`}>
                                  {atividadesProgramadas.map((item) => (
                                    <div key={`${g.gerencia}-${item.atividade}`} className="home-gerencia-ativ-item" role="listitem">
                                      <span className="home-gerencia-ativ-item__name" title={item.atividade}>{item.atividade}</span>
                                      <span className="home-gerencia-ativ-item__count">{item.programadas}</span>
                                    </div>
                                  ))}
                                </div>
                                <span className="home-gerencia-kpi__desc">
                                  {g.atividadesProgramadasTipos} tipo{g.atividadesProgramadasTipos === 1 ? "" : "s"}{atividadesRestantes ? ` (${atividadesRestantes} a mais)` : ""}
                                </span>
                              </>
                            ) : (
                              <>
                                <strong>0</strong>
                                <span className="home-gerencia-kpi__desc">Sem atividade programada</span>
                              </>
                            )}
                          </div>
                          </div>
                        </div>
                    </div>
                  );
                })
              ) : (
                <div className="home-empty">Sem dados por gerência no período.</div>
              )}
            </div>
          </div>
        </section>

        <section className="home-grid">
          <div className="home-panel home-panel-wide">
            <div className="home-panel-head">
              <h3>Painel do dia</h3>
              <span>Alertas, filas e produtividade</span>
            </div>
            <div className="home-intel-grid">
              {dashCards.map((c) => (
                <div key={c.id} className="intel-card" role="group" aria-label={c.title}>
                  <div className="intel-card__icon" aria-hidden="true">{c.icon}</div>
                  <div className="intel-card__title">{c.title}</div>
                  <div className="intel-card__desc">{c.desc}</div>
                  <div className="intel-card__tag">em breve</div>
                </div>
              ))}
              {!dashCards.length && <div className="home-empty">Nada encontrado.</div>}
            </div>
          </div>

          <div className="home-panel">
            <div className="home-panel-head">
              <h3>Recentes</h3>
              <span>Últimos acessos</span>
            </div>

            <div className="recentList" role="list">
              {recents.length ? (
                recents.map((r) => {
                  const it = allItems.find((x) => x.id === r.id);
                  const icon = r.icon || it?.icon || "??";
                  const onClick = it?.onClick;
                  const isDisabled = !onClick || !!it?.disabled;
                  return (
                    <button
                      key={r.id}
                      className="recentItem"
                      type="button"
                      onClick={() => (!isDisabled ? runItem({ ...it, icon }) : null)}
                      disabled={isDisabled}
                      role="listitem"
                    >
                      <span className="recentIcon" aria-hidden="true">{icon}</span>
                      <span className="recentText">
                        <span className="recentLabel">{r.label}</span>
                        <span className="recentTime">{timeAgo(r.ts)}</span>
                      </span>
                      <span className="recentGo" aria-hidden="true">?</span>
                    </button>
                  );
                })
              ) : (
                <div className="home-empty">Sem recentes ainda. Abra um módulo para aparecer aqui.</div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}






















